import React, { useEffect, useRef } from "react";
import { TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FormikProvider, useFormik } from "formik";
import { Youtube } from "lucide-react";
import * as Yup from "yup";
import { isValidYoutubeUrl } from "@/shared/utils/youtube/youtubeEmbedUrl";
import {
  ExperienceFieldGuide,
  ExperienceHeader,
  ExperienceOptionalLabel,
  ExperienceShell,
  type ExperienceMomentVariant,
  fieldHelperText,
  getExperienceFieldSx,
  isSeasonMomentVariant,
} from "./ExperienceUI";

interface YoutubeData {
  title?: string;
  description?: string;
  url?: string;
}

interface YoutubeExperienceProps {
  darkMode: boolean;
  data?: YoutubeData;
  momentVariant?: ExperienceMomentVariant;
  onDataChange?: (data: YoutubeData) => void;
}

const YoutubeExperience: React.FC<YoutubeExperienceProps> = ({
  darkMode,
  data,
  momentVariant = "standard",
  onDataChange,
}) => {
  const { t } = useTranslation("taascard");
  const isSeason = isSeasonMomentVariant(momentVariant);
  const isInternalUpdateRef = useRef(false);
  const fieldSx = getExperienceFieldSx(darkMode);

  const validationSchema = isSeason
    ? Yup.object({
        title: Yup.string().nullable(),
        description: Yup.string().nullable(),
        url: Yup.string()
          .required("Paste a YouTube link to continue.")
          .test("is-youtube-url", "Use a full youtube.com or youtu.be link.", (value) =>
            isValidYoutubeUrl(value)
          ),
      })
    : Yup.object({
        title: Yup.string()
          .required(t("title_is_required", { ns: "taascard" }) || "Title is required")
          .min(
            3,
            t("title_must_be_at_least_3_characters", { ns: "taascard" }) ||
              "Title must be at least 3 characters"
          ),
        description: Yup.string().nullable(),
        url: Yup.string()
          .required(t("youtube_url_is_required", { ns: "taascard" }) || "Youtube URL is required")
          .test(
            "is-youtube-url",
            t("please_enter_valid_youtube_url", { ns: "taascard" }) ||
              "Please enter a valid Youtube URL",
            (value) => isValidYoutubeUrl(value)
          ),
      });

  const formik = useFormik<YoutubeData>({
    initialValues: {
      title: data?.title ?? (isSeason ? "" : "Youtube Experience"),
      description: data?.description ?? "",
      url: data?.url ?? (isSeason ? "" : "https://www.youtube.com/watch?v=dQw4w9WgXcQ"),
    },
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: !isSeason,
    validateOnChange: false,
    onSubmit: () => {},
  });

  useEffect(() => {
    if (data && !isInternalUpdateRef.current) {
      const nextValues: YoutubeData = {
        title: data.title ?? (isSeason ? "" : "Youtube Experience"),
        description: data.description ?? "",
        url: data.url ?? (isSeason ? "" : "https://www.youtube.com/watch?v=dQw4w9WgXcQ"),
      };
      const changed = Object.keys(nextValues).some(
        (key) => formik.values[key as keyof YoutubeData] !== nextValues[key as keyof YoutubeData]
      );
      if (changed) {
        formik.setValues(nextValues);
      }
    }
    isInternalUpdateRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isSeason]);

  useEffect(() => {
    if (!onDataChange) return;
    const timeoutId = setTimeout(() => {
      isInternalUpdateRef.current = true;
      onDataChange({
        title: formik.values.title,
        description: formik.values.description,
        url: formik.values.url,
      });
    }, 0);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values]);

  return (
    <FormikProvider value={formik}>
      <ExperienceShell>
        {!isSeason && (
          <ExperienceHeader
            darkMode={darkMode}
            icon={Youtube}
            title={t("youtube_experience") || "YouTube"}
            subtitle={t("youtube_experience_subtitle")}
          />
        )}

        <ExperienceFieldGuide darkMode={darkMode}>
          {isSeason ? t("youtube_field_guide_season") : t("youtube_field_guide")}
        </ExperienceFieldGuide>

        <TextField
          fullWidth
          required={isSeason}
          label={isSeason ? <>YouTube link</> : t("youtube_url")}
          placeholder={t("youtube_url_placeholder")}
          value={formik.values.url}
          onChange={(e) => {
            formik.setFieldValue("url", e.target.value);
            formik.setFieldError("url", undefined);
          }}
          onBlur={() => formik.setFieldTouched("url", true)}
          error={formik.touched.url && !!formik.errors.url}
          helperText={fieldHelperText(
            formik.touched.url && formik.errors.url ? String(formik.errors.url) : undefined,
            isSeason ? t("youtube_embed_helper") : t("youtube_url_helper")
          )}
          sx={fieldSx}
        />

        <TextField
          fullWidth
          label={
            <>
              {t("youtube_title")}
              {isSeason && <ExperienceOptionalLabel darkMode={darkMode} />}
            </>
          }
          placeholder={
            isSeason ? t("youtube_title_placeholder_season") : t("youtube_title_placeholder")
          }
          value={formik.values.title}
          onChange={(e) => {
            formik.setFieldValue("title", e.target.value);
            formik.setFieldError("title", undefined);
          }}
          onBlur={() => formik.setFieldTouched("title", true)}
          error={formik.touched.title && !!formik.errors.title}
          helperText={fieldHelperText(
            formik.touched.title && formik.errors.title ? String(formik.errors.title) : undefined,
            isSeason ? t("youtube_title_helper_season") : t("youtube_title_helper")
          )}
          sx={fieldSx}
        />

        <TextField
          fullWidth
          multiline
          minRows={3}
          label={
            <>
              {isSeason ? t("youtube_caption") : t("youtube_video_about")}
              {isSeason && <ExperienceOptionalLabel darkMode={darkMode} />}
            </>
          }
          placeholder={
            isSeason ? t("youtube_caption_placeholder_season") : t("youtube_caption_placeholder")
          }
          value={formik.values.description}
          onChange={(e) => {
            formik.setFieldValue("description", e.target.value);
            formik.setFieldError("description", undefined);
          }}
          onBlur={() => formik.setFieldTouched("description", true)}
          error={formik.touched.description && !!formik.errors.description}
          helperText={fieldHelperText(
            formik.touched.description && formik.errors.description
              ? String(formik.errors.description)
              : undefined,
            isSeason ? t("youtube_caption_helper_season") : t("youtube_description_helper")
          )}
          sx={fieldSx}
        />
      </ExperienceShell>
    </FormikProvider>
  );
};

export default YoutubeExperience;
